import React, { useState } from "react";
import { useSelector } from "react-redux";
import BreadCrumb from "./BreadCrumb";
import Forbidden from "./Forbidden";

/**
 * A tabbed "hub" page that merges several feature pages into one nav item.
 * Each tab is permission-gated individually (only tabs the user can see are
 * shown). If the user can see none, render <Forbidden>. Mirrors the System
 * Health tab pattern; child pages render with the `embedded` prop so they drop
 * their own breadcrumb/padding.
 *
 * tabs: [{ id, label, icon, perm, render: () => <Node/> }]
 */
export default function HubShell({ title, pageTitle, tabs }) {
  const is_super_admin = useSelector((s) => s.Profile.is_super_admin);
  const permissions = useSelector((s) => s.Profile.permissions);
  const can = (p) => !p || is_super_admin || (permissions || []).includes(p);

  const visible = tabs.filter((t) => can(t.perm));
  const [active, setActive] = useState(visible[0]?.id);
  const current = visible.find((t) => t.id === active) || visible[0];

  if (!current) {
    return (
      <div style={{ padding: "clamp(1rem, 2.5vw, 1.75rem)", maxWidth: 1400, margin: "0 auto" }}>
        <Forbidden missing={tabs.map((t) => t.perm).filter(Boolean).join(" / ")} />
      </div>
    );
  }

  return (
    <div style={{ padding: "clamp(1rem, 2.5vw, 1.75rem)", maxWidth: 1400, margin: "0 auto" }}>
      <BreadCrumb title={title} pageTitle={pageTitle} />

      {/* Only show the tab strip when there's a real choice. */}
      {visible.length > 1 && (
        <div
          role="tablist"
          className="bl-glass"
          style={{ display: "flex", flexWrap: "wrap", gap: 4, padding: 6, marginBottom: "1.25rem" }}
        >
          {visible.map((tab) => {
            const isActive = tab.id === current.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(tab.id)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "0.55rem 1.1rem",
                  borderRadius: 8,
                  border: "none",
                  background: isActive ? "var(--bl-accent, #5758FC)" : "transparent",
                  color: isActive ? "#fff" : "var(--bl-text-soft)",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "background 120ms, color 120ms",
                }}
              >
                <i className={tab.icon} style={{ fontSize: 16 }} />
                {tab.label}
              </button>
            );
          })}
        </div>
      )}

      {current.render()}
    </div>
  );
}
